import _extends from '@babel/runtime-corejs3/helpers/extends';
import React__default, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Sub, SubTrigger, SubContent, Item } from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import styles from './Menu2.js';
import '../../icons/index.es.js';
import { useItemContext, usePaneContext, useMenuContext, PaneContextProvider } from './MenuContext.js';
import '../../utils/index.js';
import { DATA_TESTID_SUFFIXES as DATA_TESTID_SUFFIXES$1, KEYS as KEYS$1 } from './constants.js';
import { DATA_TESTID_SUFFIXES, KEYS } from '../Select/constants.js';
import '../../hooks/index.js';
import usePaneSearch from './Hooks/usePaneSearch.js';
import useSelectContext from '../Select/SelectContext.js';
import Check from '../../components/Checkbox/Check.js';
import styles$1 from '../Select/Select2.js';
import PaneChildren from './PaneChildren.js';
import useFocusVirtualBoundary from './Hooks/useFocusVirtualBoundary.js';
import useIsVirtualized from './Hooks/useIsVirtualized.js';
import useMaskDimensions from './Hooks/useMaskDimensions.js';
import { conditionalA11yHelpersWrapper, useIsFocusVisible } from '../../components/UIProvider/A11yHelpersContext.js';
import useId from '../../hooks/useId/useId.js';
import getSubDataTestId from '../../utils/getSubDataTestId/getSubDataTestId.js';
import { useDidUpdate } from '../../hooks/useDidUpdate/useDidUpdate.js';
import ChevronRightIcon from '../../icons/components/ChevronRightIcon.js';
import mergeRefs from '../../utils/mergeRefs/mergeRefs.js';
import ArrowLeftIcon from '../../icons/components/ArrowLeftIcon.js';

const SubMenu = /*#__PURE__*/React__default.forwardRef((_ref, ref) => {
  var _submenuDepths$intern;
  let {
    children,
    label,
    isDisabled,
    isSearchable,
    searchValue: externalSearchValue,
    onSearchValueChange,
    isSearchFilteringDisabled: isSearchFilteringDisabledForChildren = false,
    className,
    style,
    onIsOpenChange,
    ...rest
  } = _ref;
  const selectContext = useSelectContext(true);
  const withinSelect = selectContext !== null;
  const {
    subSelectTriggerProps,
    multiselect,
    subSelectPaneProps,
    isSelectedItems,
    triggerCheckedState = false,
    onTriggerCheckboxSelect
  } = selectContext !== null && selectContext !== void 0 ? selectContext : {};
  const {
    virtualIndex
  } = useItemContext();
  const [items, setItems] = useState([]);
  const internalId = useId();
  const triggerRef = useRef(null);
  const [flyoutNode, setFlyoutNode] = useState(null);
  const onRefChange = useCallback(node => setFlyoutNode(node), []);
  const stickyBoxRef = useRef(null);
  const backButtonRef = useRef(null);
  const {
    dataTestId: paneContainingTriggerDataTestId,
    childIndex,
    isInteractive: paneContainingTriggerIsInteractive,
    closeSubmenu: closeSubmenuContainingTrigger,
    zIndex: paneContainingTriggerZIndex,
    focusVirtualBoundary: focusVirtualBoundaryWithinPaneContainingTrigger,
    isVirtualized: paneContainingTriggerIsVirtualized,
    internalId: parentId
  } = usePaneContext(triggerRef);
  const triggerIndex = useMemo(() => virtualIndex !== null && virtualIndex !== void 0 ? virtualIndex : childIndex, [childIndex, virtualIndex]);
  const {
    openPaneDepth: totalOpenPaneDepth,
    registerSubmenu,
    isVirtualizationDisabled,
    submenuDepths
  } = useMenuContext();
  const {
    isFocusVisible
  } = useIsFocusVisible();
  const zIndex = useMemo(() => paneContainingTriggerZIndex + 1, [paneContainingTriggerZIndex]);
  const thisPanesDepth = (_submenuDepths$intern = submenuDepths[internalId]) !== null && _submenuDepths$intern !== void 0 ? _submenuDepths$intern : Number.MAX_SAFE_INTEGER;
  const [isPaneInDOM, setIsPaneInDOM] = useState(false);
  const [isFrameAfterPaneAddedToDOM, setIsFrameAfterPaneAddedToDOM] = useState(false);
  const [isTransitioningFromRight, setIsTransitioningFromRight] = useState(false);
  const animatedLeft = useMemo(() => thisPanesDepth < totalOpenPaneDepth, [totalOpenPaneDepth, thisPanesDepth]);
  const animatedRight = useMemo(() => thisPanesDepth > totalOpenPaneDepth || thisPanesDepth === totalOpenPaneDepth && !isPaneInDOM || isFrameAfterPaneAddedToDOM, [totalOpenPaneDepth, isFrameAfterPaneAddedToDOM, thisPanesDepth, isPaneInDOM]);
  const paneDataTestId = getSubDataTestId(paneContainingTriggerDataTestId, String(triggerIndex));
  const closeThisSubmenu = e => {
    // prevent closing the entire menu, and Radix keyboard navigation
    e.preventDefault();
    registerSubmenu(internalId, false, parentId);
  };
  const isVirtualized = useIsVirtualized(children, isVirtualizationDisabled);
  const {
    noSearchResultsNode,
    onInteractOutside,
    filteredChildren
  } = usePaneSearch({
    isSearchable,
    externalSearchValue,
    onSearchValueChange,
    items,
    animatedLeft,
    animatedRight,
    menuParentDataTestId: paneDataTestId,
    id: internalId,
    flyoutNode,
    children,
    isSearchFilteringDisabled: isSearchFilteringDisabledForChildren,
    backButtonRef,
    closeSubmenu: closeThisSubmenu,
    isVirtualized
  });
  const focusVirtualBoundaryInPane = useFocusVirtualBoundary(items, backButtonRef);
  const backButtonDataTestId = paneDataTestId === null || paneDataTestId === void 0 ? void 0 : paneDataTestId.replace(withinSelect ? DATA_TESTID_SUFFIXES.SELECT_PANE : DATA_TESTID_SUFFIXES$1.MENU_PANE, withinSelect ? DATA_TESTID_SUFFIXES.SELECT_BACK : DATA_TESTID_SUFFIXES$1.MENU_BACK);
  const triggerDataTestId = paneDataTestId === null || paneDataTestId === void 0 ? void 0 : paneDataTestId.replace(withinSelect ? DATA_TESTID_SUFFIXES.SELECT_PANE : DATA_TESTID_SUFFIXES$1.MENU_PANE, withinSelect ? DATA_TESTID_SUFFIXES.SELECT_ITEM : DATA_TESTID_SUFFIXES$1.MENU_ITEM);
  useDidUpdate(() => {
    if (isPaneInDOM) {
      setIsTransitioningFromRight(true);
      // trigger css transition
      setIsFrameAfterPaneAddedToDOM(true);
      window.requestAnimationFrame(() => setIsFrameAfterPaneAddedToDOM(false));
    }
    registerSubmenu(internalId, isPaneInDOM, parentId);
    onIsOpenChange === null || onIsOpenChange === void 0 || onIsOpenChange(isPaneInDOM);
  }, [isPaneInDOM]);

  // Omitted dependencies because this should only run on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => registerSubmenu(internalId, false), []);
  useMaskDimensions(flyoutNode, animatedLeft, thisPanesDepth, children, isVirtualized, true);
  const handlePaneTransitionEnd = () => {
    if (animatedLeft) {
      return;
    }
    if (animatedRight) {
      setIsPaneInDOM(false);
      if (isFocusVisible) {
        var _triggerRef$current;
        (_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 || _triggerRef$current.focus();
      }
      return;
    }

    // if the newly closed submenu doesn't place the focus on its trigger due to mouse events
    if (!(flyoutNode !== null && flyoutNode !== void 0 && flyoutNode.contains(document.activeElement)) && isFocusVisible) {
      if (triggerRef.current && paneContainingTriggerIsInteractive) {
        triggerRef.current.focus();
      } else if (backButtonRef.current) {
        backButtonRef.current.focus();
      }
    }
    setIsTransitioningFromRight(false);
  };
  return /*#__PURE__*/React__default.createElement(Sub, {
    open: isPaneInDOM
  }, /*#__PURE__*/React__default.createElement(SubTrigger, _extends({
    className: classNames(styles.menuItem, styles.subMenu, {
      [styles.unresponsive]: !paneContainingTriggerIsInteractive,
      [styles$1.selectedItem]: triggerCheckedState === true && !isSelectedItems
    }),
    "data-testid": triggerDataTestId,
    "aria-disabled": isDisabled,
    tabIndex: 0,
    ref: triggerRef,
    onClick: () => !isDisabled && setIsPaneInDOM(true),
    onKeyDown: e => {
      if (!paneContainingTriggerIsInteractive) {
        e.preventDefault();
        return;
      }
      if (multiselect && !isSelectedItems && KEYS.CHECK_PARENT.includes(e.key)) {
        onTriggerCheckboxSelect === null || onTriggerCheckboxSelect === void 0 || onTriggerCheckboxSelect();
        return;
      }
      if (!isDisabled && KEYS$1.OPEN_SUBMENU.includes(e.key)) {
        setIsPaneInDOM(true);
      } else if (KEYS$1.CLOSE_SUBMENU.includes(e.key)) {
        closeSubmenuContainingTrigger === null || closeSubmenuContainingTrigger === void 0 || closeSubmenuContainingTrigger(e);
      } else if (paneContainingTriggerIsVirtualized && KEYS$1.NAVIGATE_TOP.includes(e.key)) {
        e.preventDefault();
        focusVirtualBoundaryWithinPaneContainingTrigger('top');
      } else if (paneContainingTriggerIsVirtualized && KEYS$1.NAVIIGATE_BOTTOM.includes(e.key)) {
        e.preventDefault();
        focusVirtualBoundaryWithinPaneContainingTrigger('bottom');
      }
    }
  }, subSelectTriggerProps), multiselect && !isSelectedItems ? /*#__PURE__*/React__default.createElement(Check, {
    checked: triggerCheckedState,
    className: styles$1.checkbox,
    onClick: onTriggerCheckboxSelect,
    disabled: isDisabled
  }) : null, label, /*#__PURE__*/React__default.createElement(ChevronRightIcon, {
    className: styles.rightIcon
  })), /*#__PURE__*/React__default.createElement(PaneContextProvider, {
    items: items,
    setItems: setItems,
    value: {
      dataTestId: paneDataTestId,
      isInteractive: !isTransitioningFromRight && !animatedLeft && !animatedRight,
      closeSubmenu: closeThisSubmenu,
      flyoutNode,
      stickyBoxRef,
      zIndex,
      focusVirtualBoundary: focusVirtualBoundaryInPane,
      isVirtualized,
      internalId
    }
  }, /*#__PURE__*/React__default.createElement(SubContent, _extends({}, rest, {
    loop: !isSearchable,
    "data-testid": paneDataTestId,
    ref: mergeRefs(ref, onRefChange),
    onInteractOutside: onInteractOutside,
    className: classNames(className, styles.flyout, {
      [styles.animatedRight]: animatedRight,
      [styles.animatedLeft]: animatedLeft
    }),
    onTransitionEnd: handlePaneTransitionEnd,
    style: {
      ...style,
      zIndex
    }
  }, subSelectPaneProps), /*#__PURE__*/React__default.createElement("div", {
    className: classNames(styles.stickyBox, {
      [styles.isSearchable]: isSearchable
    }),
    ref: stickyBoxRef,
    style: {
      zIndex
    }
  }, /*#__PURE__*/React__default.createElement(Item, {
    className: classNames(styles.menuItem, {
      [styles.unresponsive]: isTransitioningFromRight || animatedLeft || animatedRight
    }),
    "data-testid": backButtonDataTestId,
    onSelect: closeThisSubmenu,
    ref: backButtonRef,
    onKeyDown: e => {
      if (KEYS$1.CLOSE_SUBMENU.includes(e.key)) {
        closeThisSubmenu(e);
      } else if (isVirtualized && KEYS$1.NAVIGATE_TOP.includes(e.key)) {
        e.preventDefault();
        focusVirtualBoundaryInPane('top');
      } else if (isVirtualized && KEYS$1.NAVIIGATE_BOTTOM.includes(e.key)) {
        e.preventDefault();
        focusVirtualBoundaryInPane('bottom');
      }
    }
  }, /*#__PURE__*/React__default.createElement(ArrowLeftIcon, {
    size: "small",
    className: styles.backIcon
  }), label)), noSearchResultsNode, /*#__PURE__*/React__default.createElement(PaneChildren, null, filteredChildren))));
});
var SubMenu$1 = conditionalA11yHelpersWrapper(SubMenu);

export { SubMenu$1 as default };
